/**
 * Creates the view object for working with Profile form data.
 */

import { parsePhoneNumber } from '@fs/zion-phone-number'
import { format } from '@fs/zion-locale/date-fns'
import { formatFullName } from '../formats'

const getPrivate = (canSetPublic, visibility) => (!canSetPublic ? true : visibility !== 'PUBLIC')

const getVisibility = (canSetPublic, privateValue) => (canSetPublic && !privateValue ? 'PUBLIC' : 'PRIVATE')

const emptyBirthDate = Object.freeze({ day: '', month: '', year: '', text: '' })

// Convert ISO date format (2001-07-24) to form values {day, month, year, text}
const formatBirthDateFromISO = (isoDate) => {
  if (!isoDate) return emptyBirthDate
  const [year, month, day] = isoDate.split('-')
  const yearNumber = +year
  const monthNumber = +month - 1 // Convert to 0-indexed
  const dayNumber = +day
  if (Number.isNaN(dayNumber) || Number.isNaN(monthNumber) || Number.isNaN(yearNumber)) {
    return emptyBirthDate
  }
  return {
    day: String(dayNumber),
    month: String(monthNumber + 1), // Convert back to 1-indexed for form
    year,
    text: format(new Date(yearNumber, monthNumber, dayNumber), 'PPP'),
  }
}

const getProfileItemView = ({ profile, name, label, ...rest }) => ({
  name,
  label,
  value: profile[name]?.value ?? '',
  asProfileItem: (formValues) => ({ [name]: { value: formValues[name] } }),
  ...rest,
})

const formatSex = (value, t) => {
  if (value === 'MALE') return t('gender-male.label', 'Male')
  if (value === 'FEMALE') return t('gender-female.label', 'Female')
  return ''
}

export default function getProfileView({ profile, canSetPublic, t }) {
  return {
    name: getProfileItemView({ profile, name: 'name', label: t('name.label', 'Name') }),
    contactId: getProfileItemView({ profile, name: 'contactId', label: t('contactid-profile.label', 'Contact ID') }),
    sex: {
      name: 'gender',
      label: t('gender.label', 'Sex'),
      value: profile.sex?.value ?? '',
      formattedValue: formatSex(profile.sex?.value, t),
      // Note: Sex field has @FieldPolicy(Level.RESTRICTED) which means visibility must always be PRIVATE
      // The API will reject PUBLIC visibility with error 305 "Field must be private"
      private: true,
      male: {
        label: t('gender-male.label', 'Male'),
        value: 'MALE',
      },
      female: {
        label: t('gender-female.label', 'Female'),
        value: 'FEMALE',
      },
      asProfileItem: (formValues) => ({
        sex: {
          value: formValues.gender,
          visibility: 'PRIVATE', // Sex field must always be PRIVATE per API restrictions
        },
      }),
    },
    fullName: {
      name: 'fullName',
      label: t('full-name.label', 'Full Name'),
      value: formatFullName({
        givenNames: profile.fullName?.givenName?.value,
        surnames: profile.fullName?.surname?.value,
      }),
      privateName: 'fullNamePrivate',
      canSetPublic,
      // Show as private only if BOTH fields are private, so users are aware of any public exposure
      private: getPrivate(
        canSetPublic,
        profile.fullName?.givenName?.visibility === 'PRIVATE' && profile.fullName?.surname?.visibility === 'PRIVATE'
          ? 'PRIVATE'
          : 'PUBLIC'
      ),
      givenName: {
        name: 'firstNames',
        label: t('first-names.label', 'First Names'),
        value: profile.fullName?.givenName?.value ?? '',
        visibility: profile.fullName?.givenName?.visibility,
      },
      surname: {
        name: 'lastNames',
        label: t('last-names.label', 'Last Names'),
        value: profile.fullName?.surname?.value ?? '',
        visibility: profile.fullName?.surname?.visibility,
      },
      asProfileItem: (formValues) => ({
        fullName: {
          givenName: {
            value: formValues.firstNames,
            visibility: getVisibility(canSetPublic, formValues.fullNamePrivate),
          },
          surname: {
            value: formValues.lastNames,
            visibility: getVisibility(canSetPublic, formValues.fullNamePrivate),
          },
        },
      }),
    },
    birthDate: {
      name: 'birthDate',
      label: t('birth-date.label', 'Birthdate'),
      value: formatBirthDateFromISO(profile.birthDate?.value),
    },
    country: {
      name: 'country',
      label: t('profile.country-location.label', 'Country or Location'),
      value: String(profile.location?.country?.stdRepId ?? ''),
      privateName: 'countryPrivate',
      canSetPublic,
      private: getPrivate(canSetPublic, profile.location?.country?.visibility),
      asProfileItem: ({ country: formValue, countryPrivate }) => {
        const country = formValue ? +formValue : null
        const countryIsChanging = profile.location?.country?.stdRepId !== country
        const stateOrProvince = {
          stdRepId: country === null || countryIsChanging ? null : profile.location?.stateOrProvince?.stdRepId,
          visibility:
            countryIsChanging || getPrivate(canSetPublic, countryPrivate)
              ? 'PRIVATE'
              : getVisibility(canSetPublic, profile.location?.stateOrProvince?.visibility),
        }
        return {
          location: {
            country: {
              stdRepId: country,
              visibility: getVisibility(canSetPublic, countryPrivate),
            },
            stateOrProvince,
          },
        }
      },
    },
    stateOrProvince: {
      name: 'stateOrProvince',
      label: t('profile.state-province.label', 'State or Province'),
      value: String(profile.location?.stateOrProvince?.stdRepId ?? ''),
      privateName: 'stateOrProvincePrivate',
      canSetPublic,
      private: getPrivate(canSetPublic, profile.location?.stateOrProvince?.visibility),
      asProfileItem: ({ stateOrProvince: formValue, stateOrProvincePrivate }) => {
        const value = formValue ? +formValue : null
        const country = {
          stdRepId: profile.location?.country?.stdRepId,
          visibility: profile.location?.country?.visibility,
        }
        const stateOrProvince = {
          stdRepId: value,
          visibility: getPrivate(canSetPublic, profile.location?.country?.visibility)
            ? 'PRIVATE'
            : getVisibility(canSetPublic, stateOrProvincePrivate),
        }
        return {
          location: {
            country,
            stateOrProvince,
          },
        }
      },
    },
    phone: getContactCardMethodView({
      profile,
      name: 'phone',
      label: t('profile.phone-label', 'Phone'),
      method: 'PHONE',
      type: 'UNKNOWN',
      canSetPublic,
    }),
    email: getContactCardMethodView({
      profile,
      name: 'email',
      label: t('common-ui:email.address.abbr', 'Email'),
      method: 'EMAIL',
      type: 'UNKNOWN',
      canSetPublic,
    }),
    facebook: getContactCardMethodView({
      profile,
      name: 'facebook',
      label: t('profile.facebook-username.label', 'Facebook'),
      method: 'SOCIAL',
      type: 'FACEBOOK',
      canSetPublic,
    }),
    whatsApp: getContactCardMethodView({
      profile,
      name: 'whatsApp',
      label: t('profile.whatsapp-number.label', 'WhatsApp'),
      method: 'SOCIAL',
      type: 'WHATSAPP',
      canSetPublic,
    }),
    recoveryEmail: getRecoveryEmailView({ profile, canSetPublic, t }),
    recoveryMobile: getRecoveryMobileView({ profile, canSetPublic, t }),
  }
}

function findContactMethod(methods, predicate) {
  const index = methods.findIndex(predicate)
  return { index, entry: methods[index] }
}

function replaceContactMethod(methods, index, newEntry) {
  const newMethods = methods.filter((x, i) => i !== index)
  if (newEntry) newMethods.push(newEntry)
  return { contactInfo: { methods: newMethods } }
}

function getRecoveryEmailView({ profile, canSetPublic, t }) {
  const methods = profile.contactInfo?.methods || []
  const { index, entry } = findContactMethod(
    methods,
    (x) => x.method === 'EMAIL' && x.preferredUsages?.includes('ACCOUNT_RECOVERY')
  )
  return {
    name: 'recoveryEmail',
    label: t('recovery-email.label', 'Recovery Email'),
    value: entry?.value ?? '',
    verified: !!entry?.verified,
    privateName: 'recoveryEmailPrivate',
    canSetPublic,
    private: getPrivate(canSetPublic, entry?.visibility),
    asProfileItem: (formValues) => {
      const value = formValues.recoveryEmail
      return replaceContactMethod(
        methods,
        index,
        value
          ? {
              method: 'EMAIL',
              type: 'UNKNOWN',
              value,
              visibility: getVisibility(canSetPublic, formValues.recoveryEmailPrivate),
              preferredUsages: ['ACCOUNT_RECOVERY', 'NOTIFICATIONS'],
            }
          : null
      )
    },
  }
}

function getRecoveryMobileView({ profile, canSetPublic, t }) {
  const methods = profile.contactInfo?.methods || []
  const { index, entry } = findContactMethod(
    methods,
    (x) => x.method === 'PHONE' && x.preferredUsages?.includes('ACCOUNT_RECOVERY')
  )
  const value = entry?.value ?? ''
  const country = entry?.country ?? ''
  return {
    name: 'smsNumber',
    countryName: 'smsCountry',
    label: t('recovery-mobile-number.label', 'Recovery Mobile Number'),
    value,
    country,
    formattedValue: parsePhoneNumber(value, country).format('NATIONAL'),
    verified: !!entry?.verified,
    privateName: 'smsNumberPrivate',
    canSetPublic,
    private: getPrivate(canSetPublic, entry?.visibility),
    asProfileItem: (formValues) => {
      const phoneNumber = parsePhoneNumber(formValues.smsNumber, formValues.smsCountry)
      return replaceContactMethod(
        methods,
        index,
        phoneNumber.nationalNumber
          ? {
              method: 'PHONE',
              type: 'MOBILE',
              value: phoneNumber.nationalNumber,
              country: phoneNumber.alpha3,
              visibility: getVisibility(canSetPublic, formValues.smsNumberPrivate),
              preferredUsages: ['ACCOUNT_RECOVERY', 'NOTIFICATIONS'],
            }
          : null
      )
    },
  }
}

function getContactCardMethodView({ profile, name, label, method, type, canSetPublic }) {
  const methods = profile.contactInfo?.methods || []
  const { index, entry } = findContactMethod(
    methods,
    (x) => x.method === method && x.type === type && x.preferredUsages?.includes('CONTACT_CARD')
  )
  const privateName = `${name}Private`
  return {
    name,
    label,
    value: entry?.value ?? '',
    privateName,
    canSetPublic,
    private: getPrivate(canSetPublic, entry?.visibility),
    asProfileItem: (formValues) => {
      const value = formValues[name]
      return replaceContactMethod(
        methods,
        index,
        value
          ? {
              method,
              type,
              value,
              visibility: getVisibility(canSetPublic, formValues[privateName]),
              preferredUsages: ['CONTACT_CARD'],
            }
          : null
      )
    },
  }
}
